import { Fragment } from "react/jsx-runtime";
import { generatePath, useNavigate } from "react-router-dom";
import { t } from "ttag";

import { useActiveSubscriptions } from "@/features/billing/hooks";
import { INTAKE_FORM_ROUTE } from "@/features/intake-form/router";
import type { Subscription } from "@/shared/domains";
import { INTAKE_STATUS } from "@/shared/domains";
import { Alert, Badge, Button, FileTextIcon, Heading, Text } from "@/shared/ui";

export const PendingIntakeFormAlert = () => {
  const { data: subscriptions } = useActiveSubscriptions();

  const subscriptionsWithPendingIntakeForm = subscriptions?.filter(
    (subscription) => !subscription.intakeFormCompleted,
  );

  const navigate = useNavigate();

  const handleCompleteIntake = (subscription: Subscription) => {
    if (subscription.flowStatus !== INTAKE_STATUS.REQUIRE_SCHEDULE) {
      return navigate(INTAKE_FORM_ROUTE.CHOOSE_JOURNEY, {
        state: subscription,
      });
    }

    return navigate(
      generatePath(INTAKE_FORM_ROUTE.SCHEDULE_VIDEO_CONSULTATION, {
        productId: String(subscription.currentProduct.id),
      }),
    );
  };

  if (!subscriptionsWithPendingIntakeForm?.length) {
    return null;
  }

  return (
    <Alert type="warning">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <Heading size="xx-small" color="warning" level={2}>
            {t`You have pending intake forms`}
          </Heading>

          <Text color="warning" as="p">
            {t`Please complete them to be able to join video consultations or order medication.`}
          </Text>
        </div>

        <div className="-ml-6">
          <div className="flex flex-col gap-3">
            {subscriptionsWithPendingIntakeForm?.map((subscription) => (
              <Fragment key={subscription.id}>
                <div className="grid items-center rounded-lg border border-yellow-500 bg-yellow-200 p-3 sm:grid-cols-[1fr_auto] sm:grid-rows-none">
                  <Heading
                    size="xx-small"
                    color="warning"
                    level={3}
                    icon={FileTextIcon}
                  >
                    {subscription.currentProduct.name}
                  </Heading>

                  <div className="flex flex-wrap items-center gap-2">
                    <Text size="x-small" color="warning" as="p">
                      {t`5 min`}
                    </Text>

                    <Badge
                      color="red"
                      size="x-small"
                      className="ml-auto sm:ml-0"
                    >
                      {t`Incomplete`}
                    </Badge>

                    <Button
                      onClick={() => handleCompleteIntake(subscription)}
                      size="sm"
                      className="w-full sm:w-auto"
                    >
                      {t`Complete`}
                    </Button>
                  </div>
                </div>
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    </Alert>
  );
};
